import React, { useCallback, useState } from 'react';
import { Card, Container, Row, Col, Button } from 'react-bootstrap';
import BuilderSpinnerBoundary from '@form-engine/builder-application/components/spinner-boundary';
import { formatDistanceToNow } from '@form-engine/core-util/day';
import BuilderCard from '@form-engine/builder-application/components/card';
import {
    ApiListOptions,
    ApiListResult,
    useProgressiveList,
} from '@form-engine/builder-application/hooks/use-api-model';
import { Link, Route, Routes, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { ArrowDownCircle } from 'react-bootstrap-icons';
import { useFormList } from '@form-engine/builder-application/hooks/use-form-api';
import BuilderFormSelect from '@form-engine/builder-application/components/select';
import { LogDefinition } from '@form-engine/core-application/types/form';
import BuilderHubViewHeader from '../view-header';
import { i18n } from '@form-engine/builder-application';
import useSessionLogApi from '@form-engine/builder-application/hooks/use-session-log-api';
import IconButton from '@form-engine/builder-icon-button';
import BuilderSessionLogDetailView from './detail';
import { CSSVariableName } from '@form-engine/core-util/typings';

export function exportLogsAsFile(
    source: LogDefinition[],
    filename = 'export.json',
    type = 'application/json'
) {
    const data = source.map((log) => {
        try {
            return {
                ...log,
                value: log.value ? JSON.parse(log.value) : undefined,
            };
        } catch {
            console.warn('exporting invalid log ', log.id);
            return log;
        }
    });

    const blob = new Blob([JSON.stringify(data, undefined, 2)], { type });
    const toggle = document.createElement('a');
    toggle.download = filename;
    toggle.href = URL.createObjectURL(blob);
    toggle.click();
}

export function useExportLogsCallback(listOptions: ApiListOptions) {
    const logsApi = useSessionLogApi();

    return useCallback(async () => {
        const logs = (await logsApi.list(
            listOptions
        )) as ApiListResult<LogDefinition>;
        console.log(logs);
        exportLogsAsFile(logs, `${logs[0]?.sessionId || 'untitled'}.json`);
    }, [listOptions]);
}

function BuilderSessionLogsListingItem({
    path,
    items,
    activeLogId,
}: {
    path: string;
    items: LogDefinition[];
    activeLogId?: string;
}) {
    const [initialItem] = items;

    const handleExport = useExportLogsCallback(
        useMemo(
            () => ({
                attributes: ['id', 'formId', 'sessionId', 'value', 'createdAt'],
                sort: { createdAt: 'asc' },
                filter: {
                    sessionId: initialItem?.sessionId,
                },
            }),
            []
        )
    );

    if (!initialItem) return null;

    return (
        <Col xs={12}>
            <h6 className="py-2">
                <div className="d-flex flex-row align-items-center gap-1">
                    <div>{initialItem.form?.name}</div>{' '}
                    <IconButton
                        onClick={handleExport}
                        title={i18n.c('session-logs.group.export')}
                        className="text-muted text-xs p-0"
                    >
                        <ArrowDownCircle />
                    </IconButton>
                </div>
                <div className="text-muted text-xs mt-1">
                    <i18n.Node
                        id="session-logs.item.submitted"
                        date={formatDistanceToNow(
                            new Date(initialItem.createdAt ?? '')
                        )}
                    />
                </div>
            </h6>
            <div className="d-flex flex-column gap-2">
                {items.map((item) => {
                    const workflow = item.workflowId
                        ? item.form?.workflowLayers?.find(
                              (wfl) => wfl.id === item.workflowId
                          )
                        : undefined;
                    return (
                        <BuilderCard
                            key={item.id}
                            as={Link}
                            to={`${path}/${item.id}`}
                            className={activeLogId === item.id ? 'active' : ''}
                        >
                            <Card.Body>
                                {item.form && (
                                    <Card.Title className="text-xs">
                                        {workflow?.name ?? ''}{' '}
                                        <span className="text-muted">
                                            #{item.id?.slice(0, 8)}
                                        </span>
                                    </Card.Title>
                                )}
                                <Card.Text className="text-xs text-black-50">
                                    <i18n.Node
                                        id="session-logs.item.submitted"
                                        date={formatDistanceToNow(
                                            new Date(item.updatedAt ?? '')
                                        )}
                                    />
                                </Card.Text>
                            </Card.Body>
                        </BuilderCard>
                    );
                })}
            </div>
        </Col>
    );
}

interface FilterOption {
    value: string;
    label: string;
}

function useLogList() {
    const [formFilter, setFormFilter] = useState<FilterOption[]>([]);

    const { state: forms = [], loading: formsLoading } = useFormList(
        useMemo(
            () => ({
                attributes: [
                    'id',
                    'name',
                    'updatedAt',
                    'createdAt',
                    'tags',
                    'thumbnail',
                    'workflowLayers',
                ],
            }),
            []
        )
    );

    const listParams = useMemo(() => {
        const params: ApiListOptions = {
            attributes: [
                'id',
                'createdAt',
                'updatedAt',
                'formId',
                'sessionId',
                'workflowId',
            ],
            filter: { sessionId: { $ne: null } },
        };

        if (!formFilter.length) return params;

        params.filter ??= {};
        params.filter.formId = {
            $in: formFilter.map((option) => option.value),
        };

        return params;
    }, [formFilter]);

    const {
        state: logs = [],
        next,
        complete,
        loading: logsLoading,
    } = useProgressiveList(useSessionLogApi(), listParams);

    const state = useMemo(() => {
        return logs
            .map((log) => ({
                ...log,
                form: forms.find((form) => form.id === log.formId),
            }))
            .filter((log) => Boolean(log.form))
            .sort(
                (a, b) =>
                    new Date(b.createdAt ?? '').getTime() -
                    new Date(a.createdAt ?? '').getTime()
            );
    }, [forms, logs]);

    return {
        loading: formsLoading || logsLoading,
        formFilter,
        setFormFilter,
        forms,
        logs: state,
        next,
        complete,
    };
}

export interface BuilderSessionLogsListingProps
    extends React.HTMLAttributes<HTMLElement> {
    as?: React.ElementType;
    path?: string;
    showDetail?: boolean;
    detailProps?: Omit<
        React.ComponentProps<typeof BuilderSessionLogDetailView>,
        'closePath'
    >;
}

export default function BuilderSessionLogsListing({
    as: Component = 'div',
    path = '/hub/session-logs',
    showDetail = true,
    detailProps = {},
    ...props
}: BuilderSessionLogsListingProps) {
    const {
        logs = [],
        forms = [],
        loading,
        complete,
        next,
        formFilter,
        setFormFilter,
    } = useLogList();

    const { '*': activeLogId } = useParams();

    const sessionMap = useMemo(() => {
        const result = new Map<string, typeof logs>();

        for (const log of logs) {
            if (!log.sessionId) continue;

            if (!result.has(log.sessionId)) {
                result.set(log.sessionId, []);
            }

            result.get(log.sessionId)?.push(log);
        }

        for (const logs of result.values()) {
            logs.sort(
                (a, b) =>
                    Date.parse(a.createdAt ?? '') -
                    Date.parse(b.createdAt ?? '')
            );
        }

        return result;
    }, [logs]);

    return (
        <>
            <Component {...props}>
                <div className="feb-view">
                    <div className="feb-view-inner p-0">
                        <Container fluid>
                            <BuilderHubViewHeader
                                title={i18n.c('session-logs.title')}
                            >
                                <BuilderFormSelect.Advanced
                                    className="text-xs"
                                    style={{ position: 'sticky' }}
                                    value={formFilter}
                                    onChange={({
                                        target,
                                    }: {
                                        target: { value: FilterOption[] };
                                    }) => setFormFilter(target.value)}
                                    placeholder={i18n.c('session-logs.filter')}
                                    options={forms.map((form) => ({
                                        value: form.id,
                                        label: form.name,
                                    }))}
                                    isMulti={true}
                                />
                            </BuilderHubViewHeader>
                            <BuilderSpinnerBoundary show={loading} bar={false}>
                                {logs.length > 0 ? (
                                    <Row
                                        style={{
                                            ['--bs-gutter-y' as CSSVariableName]:
                                                '.75rem',
                                        }}
                                    >
                                        {Array.from(sessionMap.entries()).map(
                                            ([sessionId, items]) => {
                                                return (
                                                    <BuilderSessionLogsListingItem
                                                        key={sessionId}
                                                        items={items}
                                                        activeLogId={
                                                            activeLogId
                                                        }
                                                        path={path}
                                                    />
                                                );
                                            }
                                        )}
                                    </Row>
                                ) : (
                                    <p className="text-center text-muted">
                                        <i18n.Node id="session-logs.no-logs" />
                                    </p>
                                )}
                                {!complete && (
                                    <div className="text-center my-3">
                                        <Button
                                            size="sm"
                                            onClick={next}
                                            variant="outline-dark"
                                        >
                                            <i18n.Node id="session-logs.more" />{' '}
                                            <ArrowDownCircle />
                                        </Button>
                                    </div>
                                )}
                            </BuilderSpinnerBoundary>
                        </Container>
                    </div>
                </div>
            </Component>
            {showDetail && (
                <Routes>
                    <Route
                        path=":id"
                        element={
                            <BuilderSessionLogDetailView
                                id="sessionLogDetailCol"
                                closePath={path}
                                {...detailProps}
                            />
                        }
                    />
                </Routes>
            )}
        </>
    );
}
